import { useContext, useState } from 'react';
import LearnerTaskContent from './LearnerTaskContent';
import useModifyRequest from '../../../../../hooks/useModifyRequest';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { LearnerActivity } from 'models/LearnerCourse';
import CompleteCourseModal from 'components/modals/CompleteCourseModal';

type LearnerTaskProps = {
    task: LearnerActivity;
};

export default function LearnerTask({ task }: LearnerTaskProps): JSX.Element {
    const learnerCourse = useContext(LearnerCourseContext).learnerCourse!;
    const { previewMode, setCourseState, isFinalActivity } = useContext(LearnerCourseContext);
    const [showCompleteCourseModal, setShowCompleteCourseModal] = useState<boolean>(false);

    const { modifyData: setActivityCompletion, errors: errorsSettingActivityStatus } = useModifyRequest(
        `learner/courses/${learnerCourse.enrollmentId}/activity/${task.activityStatusId}/complete`,
        'POST',
    );

    const toggleTaskCompletion = async () => {
        const completed: Date | null = !task.completed ? new Date() : null;
        const newActivityStatus = { completed: completed };

        if (!previewMode) {
            const response = await setActivityCompletion(newActivityStatus);

            if (response.errors && response.errors.length) {
                return { errors: response.errors };
            }
        }

        setCourseState((prev) => {
            if (!prev) return prev;

            const moduleIndex = prev.modules.findIndex((module) => module.id === task.moduleId);

            const activityIndex = prev.modules[moduleIndex].activities.findIndex(
                (activity) => activity.activityStatusId === task.activityStatusId,
            );

            const newModules = [...prev.modules];
            const newActivities = [...newModules[moduleIndex].activities];

            newActivities[activityIndex] = {
                ...newActivities[activityIndex],
            };

            newActivities[activityIndex].completed = !!completed;

            newModules[moduleIndex] = {
                ...newModules[moduleIndex],
                activities: newActivities,
            };

            return {
                ...prev,
                modules: newModules,
            };
        });

        if (isFinalActivity) {
            setShowCompleteCourseModal(true);
        }

        return { value: true };
    };

    return (
        <>
            {showCompleteCourseModal && <CompleteCourseModal />}
            <LearnerTaskContent
                task={task}
                toggleTaskCompletion={toggleTaskCompletion}
                toggleCompletionErrors={errorsSettingActivityStatus}
            />
        </>
    );
}
